import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { Modal, Field } from "@/components/ui-mm/Modal";
import { Switch } from "@/components/ui/switch";
import CurrencyPicker from "@/components/mm/CurrencyPicker";
import { Avatar } from "@/components/mm/Header";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useFxRates } from "@/hooks/useFxRates";
import { PALS, ENGLISH_FONTS, BENGALI_FONTS } from "@/data/constants";
import { applyTheme, DEFAULT_UI } from "@/lib/theme";
import { api, errMsg } from "@/lib/api";
import { t } from "@/lib/i18n";
import BankNotificationSettings from "@/components/mm/BankNotificationSettings";
import { nativeBankNotifications } from "@/lib/nativeBankNotifications";
import MfaSettings from "@/components/mm/MfaSettings";
import { AppLockSettings } from "@/components/mm/AppLock";

const Seg = ({ v, set, opts, testId }) => (
  <div className="mm-seg flex-wrap">{opts.map(([k, l]) => <button key={k} className={v === k ? "on" : ""} onClick={() => set(k)} data-testid={`${testId}-${k}`}>{l}</button>)}</div>
);
const TABS = [["profile", "Profile"], ["general", "General"], ["bank", "Bank capture"]];

function shrink(file) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => {
      const s = 256, c = document.createElement("canvas");
      const m = Math.min(img.width, img.height);
      c.width = c.height = s;
      c.getContext("2d").drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, s, s);
      URL.revokeObjectURL(img.src);
      res(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = rej;
    img.src = URL.createObjectURL(file);
  });
}

function ProfileTab({ onClose }) {
  const { user, setUser, finishAccountDeletion } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [pic, setPic] = useState(user.picture || null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleteMfaCode, setDeleteMfaCode] = useState("");
  const [subscription, setSubscription] = useState(null);
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    if (!/^image\//.test(f.type)) return toast.error("Please choose an image file");
    try { setPic(await shrink(f)); } catch (x) { toast.error("Could not read that image"); }
  };
  const save = async () => {
    if (!name.trim()) return toast.error(t("Your name"));
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { name: name.trim(), picture: pic || "" });
      setUser(data); onClose();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const openDeletion = async () => {
    setDeleting(true);
    try { setSubscription((await api.get("/subscription/status")).data); } catch { setSubscription(null); }
  };
  const erase = async () => {
    if (deletePhrase !== "DELETE MY ACCOUNT") return toast.error("Type DELETE MY ACCOUNT exactly.");
    if (user.has_password && !deletePassword) return toast.error("Enter your current password.");
    setBusy(true);
    try {
      const { data } = await api.delete("/auth/account", { data: { confirmation: deletePhrase, password: deletePassword, mfa_code: deleteMfaCode } });
      if (!data?.verified) throw new Error("Deletion was not verified");
      if (nativeBankNotifications.available()) await nativeBankNotifications.clearPending().catch(() => {});
      await finishAccountDeletion(); onClose(); toast.success("Your BudgetPulse account and active server data were deleted.");
    } catch (error) { toast.error(errMsg(error)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="settings-profile-tab">
      <div className="flex items-center gap-4">
        <Avatar user={{ ...user, name, picture: pic }} className="h-20 w-20 text-2xl" />
        <div className="flex flex-col gap-2">
          <button className="mm-btn ghost sm" onClick={() => ref.current.click()} data-testid="profile-photo-change-button"><Camera className="h-4 w-4" /> {t("Change photo")}</button>
          {pic && <button className="mm-btn ghost sm" onClick={() => setPic(null)} data-testid="profile-photo-remove-button"><Trash2 className="h-4 w-4" /> {t("Remove photo")}</button>}
          <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid="profile-photo-input" />
        </div>
      </div>
      <Field label={t("Your name")}><input className="mm-input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></Field>
      <p className="text-xs text-[var(--mu)]">{user.email}</p>
      <button className="mm-btn" onClick={save} disabled={busy} data-testid="profile-save-button">{t("Save profile")}</button>
      <MfaSettings user={user} setUser={setUser} onSignedOut={finishAccountDeletion} />
      <AppLockSettings />
      <div className="mt-8 rounded-[var(--ri)] border border-[var(--dg)]/40 p-4" data-testid="delete-account-section">
        <h3 className="font-semibold text-[var(--dg)]">Delete account & server data</h3>
        <p className="mt-1 text-xs leading-relaxed text-[var(--mu)]">This permanently removes your profile, transactions, income, salary packages, savings, goals, recurring schedules, notes, settings, AI tips, local subscription records and sessions from the active BudgetPulse database. Download a backup first if you need a copy.</p>
        {!deleting ? <button className="mm-btn danger mt-3 sm" onClick={openDeletion}>Start permanent deletion</button> : <div className="mt-4 space-y-3">
          {subscription && !["free","expired"].includes(subscription.status) && <div className="rounded-[var(--ri)] border border-[var(--wn)] p-3 text-xs"><b>External subscription action may still be required.</b><p className="mt-1 text-[var(--mu)]">Deleting BudgetPulse data does not cancel billing or erase records held independently by a payment provider. Cancel there first if applicable. BudgetPulse will ignore later webhooks for a deleted account.</p></div>}
          {user.has_password && <Field label="Current password"><input className="mm-input" type="password" autoComplete="current-password" value={deletePassword} onChange={(e)=>setDeletePassword(e.target.value)} /></Field>}
          {user.mfa_enabled && <Field label="Authenticator or recovery code"><input className="mm-input num" autoComplete="one-time-code" value={deleteMfaCode} onChange={(e)=>setDeleteMfaCode(e.target.value)} /></Field>}
          <Field label="Type DELETE MY ACCOUNT to confirm"><input className="mm-input" autoCapitalize="characters" autoComplete="off" value={deletePhrase} onChange={(e)=>setDeletePhrase(e.target.value)} data-testid="delete-account-confirmation" /></Field>
          <p className="text-xs text-[var(--dg)]">There is no undo. This action signs out every device.</p>
          <div className="flex flex-wrap gap-2"><button className="mm-btn ghost sm" disabled={busy} onClick={()=>{setDeleting(false);setDeletePhrase("");setDeletePassword("");setDeleteMfaCode("");}}>Keep my account</button><button className="mm-btn danger sm" disabled={busy || deletePhrase !== "DELETE MY ACCOUNT" || (user.has_password && !deletePassword) || (user.mfa_enabled && !deleteMfaCode)} onClick={erase} data-testid="delete-account-button">{busy ? "Deleting…" : "Permanently delete everything"}</button></div>
        </div>}
      </div>
    </div>
  );
}

function GeneralTab() {
  const { db, saveSettings } = useData();
  const confirm = useConfirm();
  const fx = useFxRates();
  const prefs = db.settings.prefs;
  const [mins, setMins] = useState(prefs.editMinutes);
  const save = (p) => saveSettings({ prefs: { ...prefs, ...p } }).catch(() => {});
  const setCur = async (c) => {
    if (c === prefs.currency) return;
    if (await confirm(`${c}?\n\nExisting amounts are not converted; only the currency shown changes.`)) save({ currency: c });
  };
  return (
    <div className="space-y-5" data-testid="settings-general-tab">
      <Field label={t("Main currency")}><CurrencyPicker value={prefs.currency} onChange={setCur} codes={fx.codes} testId="settings-currency-picker" /></Field>
      <Field label={t("Language")}><Seg v={prefs.lang} set={(v) => save({ lang: v })} opts={[["en", "English"], ["bn", "বাংলা"]]} testId="settings-language" /></Field>
      <Field label="Tax country (separate from display currency)"><select className="mm-input" value={prefs.taxCountry || ""} onChange={(e)=>save({taxCountry:e.target.value})}><option value="">Not selected</option><option value="AU">Australia</option><option value="BD">Bangladesh</option></select></Field>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2"><Field label="Tax residency"><select className="mm-input" value={prefs.taxResidency || "unspecified"} onChange={(e)=>save({taxResidency:e.target.value})}><option value="unspecified">Not specified</option><option value="resident">Resident</option><option value="nonresident">Non-resident</option></select></Field><Field label="Income / assessment year"><input className="mm-input" maxLength={20} placeholder="e.g. 2025–26" value={prefs.taxYear || ""} onChange={(e)=>save({taxYear:e.target.value})} /></Field></div>
      <div className="flex items-start justify-between gap-4 rounded-[var(--ri)] border border-[var(--bd)] p-4">
        <div><div className="font-semibold">{t("Muslim mode")}</div><p className="mt-1 text-xs text-[var(--mu)]">{t("Shows Quran & Hadith quotes and the Zakat calculator. Turn off for money quotes from famous people.")}</p></div>
        <Switch checked={prefs.faith !== "other"} onCheckedChange={(v) => save({ faith: v ? "muslim" : "other" })} data-testid="settings-muslim-toggle" />
      </div>
      <div className="flex items-start justify-between gap-4 rounded-[var(--ri)] border border-[var(--bd)] p-4">
        <div><div className="font-semibold">Button sounds</div><p className="mt-1 text-xs text-[var(--mu)]">Short sound after tapping buttons. Off means silence.</p></div>
        <Switch checked={prefs.sound !== false} onCheckedChange={(v) => save({ sound: v })} aria-label="Button sounds" data-testid="settings-sound-toggle" />
      </div>
      <div className="flex items-start justify-between gap-4 rounded-[var(--ri)] border border-[var(--bd)] p-4">
        <div><div className="font-semibold">Dashboard reminders</div><p className="mt-1 text-xs text-[var(--mu)]">Show in-app reminders for budgets, debts and recurring items. No push permission required.</p></div>
        <Switch checked={prefs.remindersEnabled !== false} onCheckedChange={(v) => save({ remindersEnabled: v })} aria-label="Dashboard reminders" />
      </div>
      <Field label={t("Edit window (minutes)")}>
        <input className="mm-input" type="number" min="1" max="1440" value={mins} onChange={(e) => setMins(e.target.value)} onBlur={() => save({ editMinutes: Math.max(1, Math.min(1440, parseInt(mins, 10) || 30)) })} data-testid="settings-edit-minutes-input" />
      </Field>
    </div>
  );
}

function DesignTab({ ui, set }) {
  return (
    <div data-testid="settings-design-tab">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--mu)]">{t("Colour palette")}</div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {PALS.map((p) => (
          <button key={p.id} onClick={() => set("pal", p.id)} className={`rounded-[var(--ri)] border-2 p-2 text-left text-[11px] font-semibold transition-transform hover:-translate-y-0.5 ${ui.pal === p.id ? "border-[var(--ac)]" : "border-[var(--bd)]"}`} data-testid={`design-palette-${p.id}`}>
            <div className="mb-1.5 h-7 rounded-md" style={{ background: `linear-gradient(135deg, ${p.g[0]}, ${p.g[1]})` }} />{p.n}
          </button>
        ))}
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field label={t("Mode")}><Seg v={ui.mode} set={(v) => set("mode", v)} opts={[["auto", t("Auto")], ["light", t("Light")], ["dark", t("Dark")]]} testId="design-mode" /></Field>
        <Field label={t("Corners")}><Seg v={ui.corner} set={(v) => set("corner", v)} opts={[["sharp", t("Sharp")], ["soft", t("Soft")], ["round", t("Round")]]} testId="design-corner" /></Field>
      </div>
      <details className="mt-5 rounded-[var(--ri)] border-2 border-[var(--ac)] p-3" data-testid="design-english-fonts">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[var(--ac)] [&::-webkit-details-marker]:hidden">
          <span>English Fonts</span><span aria-hidden="true">▾</span>
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {Object.entries(ENGLISH_FONTS).map(([k, [n, css]]) => (
            <button key={k} type="button" onClick={() => set("latinFont", k)} className={`rounded-[var(--ri)] border-2 px-3 py-2 text-left ${ui.latinFont === k ? "border-[var(--ac)]" : "border-[var(--bd)]"}`} style={{ fontFamily: css }} data-testid={`design-latin-font-${k}`}>
              <div className="text-lg leading-tight" style={{ fontFamily: css }}>Budget notes</div><div className="text-xs" style={{ fontFamily: css }}>{n}</div>
            </button>
          ))}
        </div>
      </details>
      <details className="mt-4 rounded-[var(--ri)] border-2 border-[var(--ac)] p-3" data-testid="design-bengali-fonts">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[var(--ac)] [&::-webkit-details-marker]:hidden">
          <span>বাংলা ফন্টস</span><span aria-hidden="true">▾</span>
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {Object.entries(BENGALI_FONTS).map(([k, [n, css]]) => (
            <button key={k} type="button" onClick={() => set("bengaliFont", k)} className={`rounded-[var(--ri)] border-2 px-3 py-2 text-left ${ui.bengaliFont === k ? "border-[var(--ac)]" : "border-[var(--bd)]"}`} style={{ fontFamily: css }} data-testid={`design-bengali-font-${k}`}>
              <div className="text-lg leading-tight" style={{ fontFamily: css }}>সঞ্চয় ও খরচ</div><div className="text-xs" style={{ fontFamily: css }}>{n}</div>
            </button>
          ))}
        </div>
      </details>
      <p className="mt-3 text-xs text-[var(--mu)]">{t("Financial amounts keep a fixed, readable numeric font.")}</p>
    </div>
  );
}

export default function SettingsDialog({ open, tab: initTab, onClose, onGuide, designOnly = false }) {
  const { db, saveSettings } = useData();
  const prefs = db.settings.prefs;
  const [tab, setTab] = useState("general");
  const [ui, setUi] = useState({ ...DEFAULT_UI, ...prefs.ui });
  useEffect(() => { if (open) { setUi({ ...DEFAULT_UI, ...prefs.ui }); setTab(initTab || "general"); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => { const n = { ...ui, [k]: v }; setUi(n); applyTheme(n); };
  const dirty = JSON.stringify(ui) !== JSON.stringify(prefs.ui);
  const close = async () => { if (dirty) await saveSettings({ prefs: { ...db.settings.prefs, ui } }).catch(() => applyTheme(prefs.ui)); onClose(); };

  return (
    <Modal open={open} onClose={close} title={t(designOnly ? "Design" : "Settings")} testId={designOnly ? "design-dialog" : "settings-dialog"} wide>
      {!designOnly && <div className="mm-seg mb-5">
        {TABS.map(([k, l]) => <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`}>{t(l)}</button>)}
      </div>}
      {!designOnly && tab === "profile" && <ProfileTab onClose={close} />}
      {!designOnly && tab === "general" && <GeneralTab />}
      {designOnly && <DesignTab ui={ui} set={set} />}
      {!designOnly && tab === "bank" && <BankNotificationSettings />}
      <div className="mt-6 flex justify-between gap-2">
        {designOnly ? <button className="mm-btn ghost sm" onClick={() => { setUi(DEFAULT_UI); applyTheme(DEFAULT_UI); }} data-testid="design-reset-button">{t("↺ Reset design")}</button> : <span />}
        <button className="mm-btn" onClick={close} data-testid="settings-done-button">{t("Done")}</button>
      </div>
    {!designOnly && <button className="mm-btn ghost" onClick={async () => { await close(); onGuide(); }}>Show Quick Guide</button>}</Modal>
  );
}
